import { Link, Navigate, useLocation } from 'react-router-dom';
import { useMe } from '../lib/auth.js';
import { useOrders, shortDate } from '../lib/dashboard.js';
import type { Order } from '@z9-store/core';

export function OrdersPage(): JSX.Element {
  const me = useMe();
  const location = useLocation();
  const q = useOrders();

  if (me.isLoading) return <p className="text-sm text-slate-500">Loading...</p>;
  if (!me.data) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (q.isLoading) return <p className="text-sm text-slate-500">Loading orders...</p>;
  if (q.isError) {
    return (
      <div className="rounded bg-red-50 p-4 text-sm text-red-700" data-testid="orders-error">
        Could not load orders: {(q.error as Error).message}
      </div>
    );
  }
  const orders = q.data ?? [];
  if (orders.length === 0) {
    return (
      <div data-testid="orders-empty" className="rounded bg-slate-100 p-6 text-sm text-slate-700">
        You have not placed an order yet.{' '}
        <Link to="/" className="font-medium text-slate-900 underline">
          Browse the catalog
        </Link>
        .
      </div>
    );
  }
  return (
    <div data-testid="orders" className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Your orders</h1>
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Items</th>
              <th className="px-3 py-2">Placed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {orders.map((o) => (
              <OrderRow key={o.orderId} order={o} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrderRow({ order }: { order: Order }): JSX.Element {
  return (
    <tr data-testid={`order-row-${order.orderId}`}>
      <td className="px-3 py-2 font-mono text-xs">
        <Link to={`/orders/${encodeURIComponent(order.orderId)}/success`} className="text-slate-900 underline hover:text-slate-700">
          {order.orderId.slice(0, 12)}
        </Link>
      </td>
      <td className="px-3 py-2 text-slate-700">{order.type}</td>
      <td className="px-3 py-2">
        <StatusBadge status={order.status} />
      </td>
      <td className="px-3 py-2 text-slate-700">{order.cartItems.length}</td>
      <td className="px-3 py-2 text-slate-700">{shortDate(order.createdAt)}</td>
    </tr>
  );
}

function StatusBadge({ status }: { status: Order['status'] }): JSX.Element {
  const map: Record<Order['status'], string> = {
    pending: 'bg-amber-50 text-amber-800',
    'paid-but-unfulfilled': 'bg-amber-100 text-amber-900',
    fulfilled: 'bg-green-50 text-green-800',
    cancelled: 'bg-slate-100 text-slate-700',
    refunded: 'bg-slate-100 text-slate-700'
  };
  return <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${map[status]}`}>{status}</span>;
}
